import { Link } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, ArrowRight, Video, Building } from 'lucide-react';
import { events, getNextEvent } from '../data/events';

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const Events = () => {
  const nextEvent = getNextEvent();
  const otherEvents = events.filter(e => e.id !== nextEvent?.id);

  return (
    <div data-testid="events-page">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-white via-gray-50 to-brand-turquoise/5">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="badge-lime inline-block mb-6">Connect & Learn</span>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl text-brand-blue leading-tight mb-6">
              Workshops & Events
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Join our interactive workshops, webinars, and community events. 
              Learn, connect, and grow with fellow STEAM professionals.
            </p>
          </div>
        </div>
      </section>

      {/* Next Workshop - Featured */}
      {nextEvent && (
        <section className="section-padding bg-white" data-testid="next-event-section">
          <div className="container-custom">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-8">
              Next Workshop
            </h2>

            <div
              className="bg-gradient-to-r from-brand-turquoise/10 to-brand-lime/10 rounded-3xl p-8 md:p-12 border border-brand-turquoise/20"
              data-testid={`featured-event-${nextEvent.id}`}
            >
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  {nextEvent.featured && (
                    <span className="badge-lime inline-block mb-4">Featured</span>
                  )}
                  <h3 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-4">
                    {nextEvent.title}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {nextEvent.description}
                  </p>

                  <div className="flex flex-wrap gap-6 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="w-5 h-5 text-brand-turquoise" />
                      <span>{formatDate(nextEvent.date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <Clock className="w-5 h-5 text-brand-turquoise" />
                      <span>{nextEvent.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      {nextEvent.modality === 'Online' ? (
                        <Video className="w-5 h-5 text-brand-turquoise" />
                      ) : (
                        <Building className="w-5 h-5 text-brand-turquoise" />
                      )}
                      <span>{nextEvent.modality}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-5 h-5 text-brand-turquoise" />
                      <span>{nextEvent.location}</span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col justify-center items-start lg:items-end gap-4">
                  <div className="text-right">
                    <p className="text-sm text-gray-500 mb-1">Price</p>
                    <p className="font-accent font-bold text-2xl text-brand-blue">
                      {nextEvent.price}
                    </p>
                  </div>
                  {nextEvent.spotsLeft && (
                    <p className="text-sm text-brand-magenta font-medium">
                      {nextEvent.spotsLeft} spots left
                    </p>
                  )}
                  <a
                    href={nextEvent.registrationLink}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid={`register-btn-${nextEvent.id}`}
                  >
                    Register Now
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Other Events */}
      <section className="section-padding bg-gray-50" data-testid="other-events-section">
        <div className="container-custom">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-brand-blue mb-8">
            Upcoming Events
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-2xl p-8 border border-gray-100 card-hover"
                data-testid={`event-card-${event.id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className={`text-xs font-medium px-3 py-1 rounded-full ${
                    event.modality === 'Online' 
                      ? 'bg-brand-turquoise/10 text-brand-turquoise'
                      : event.modality === 'Hybrid'
                      ? 'bg-brand-lime/20 text-brand-blue'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {event.modality}
                  </span>
                  {event.featured && (
                    <span className="text-xs font-medium text-brand-magenta">Featured</span>
                  )}
                </div>

                <h3 className="font-heading font-bold text-xl text-brand-blue mb-3">
                  {event.title}
                </h3>
                <p className="text-gray-600 text-sm mb-6 line-clamp-2">
                  {event.description}
                </p>

                <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-6">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span>{formatDate(event.date)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{event.time}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <span className="font-accent font-bold text-lg text-brand-blue">
                    {event.price}
                  </span>
                  <a
                    href={event.registrationLink}
                    className="btn-secondary text-sm py-2 px-4"
                    data-testid={`register-btn-${event.id}`}
                  >
                    Register
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-brand-blue">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
            Want us to host an event at your organization?
          </h2>
          <p className="text-gray-300 mb-8 max-w-xl mx-auto">
            We offer custom workshops and speaking engagements for schools, 
            companies, and communities.
          </p>
          <Link to="/consulting" className="btn-primary bg-brand-magenta hover:bg-pink-600">
            Get in Touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Events;
